/*
PURPOSE: Editorial manifesto page, destination of the Home2 hero's
"READ OUR MANIFESTO" CTA, so its hero must read continuously with Home2.
USE CASES:
- Public /manifesto route.
DOMAIN BOUNDARIES:
- Presentation only. The manifesto body is hardcoded and reproduced from
  the original, with one intentional copy edit: em-dashes replaced by
  commas (brand voice: no em-dashes). Otherwise unchanged.
- MANIFESTO_PAGE_VIEW analytics preserved. Dan portrait + coral wordmark
  kept (black wordmark works on the cream surface). No wave imagery.
- No imports from design-sandbox/.
*/

import React, { useEffect } from 'react';
import danProfilePic from '../../../assets/images/about/DanProfilePic.png';
import coralWordmarkBlack from '../../../assets/images/coral-wordmark-black.png';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import { EditorialSection, Eyebrow, DisplayHeading } from '../../../components/editorial';
import PublicPageMeta from '../../../components/MetaHead/PublicPageMeta';

const Manifesto: React.FC = () => {
  useEffect(() => {
    trackEvent(EventName.MANIFESTO_PAGE_VIEW, {});
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-ivory">
      <PublicPageMeta
        title="Manifesto | wearecoral"
        description="Music thrives on intention. Dan Gooden on why he built Coral, and an invitation to tend music's commons rather than mine it."
        path="/manifesto"
      />
      <EditorialSection background="ivory" topRule={false} id="manifesto-hero">
        <DisplayHeading as="h1" scale="display">
          Music thrives on intention.
        </DisplayHeading>
      </EditorialSection>

      <EditorialSection background="cream" id="manifesto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <div className="text-center md:text-left mb-8">
              <img
                src={danProfilePic}
                alt="Dan Gooden"
                className="w-32 h-32 md:w-40 md:h-40 rounded-full mx-auto md:mx-0 mb-4"
              />
              <p className="font-cormorant text-editorial-muted text-2xl">Dan Gooden</p>
              <p className="text-editorial-eyebrow uppercase text-editorial-accent">CREATOR</p>
            </div>
            <Eyebrow>MY MANIFESTO</Eyebrow>
            <DisplayHeading as="h2" scale="heading" className="mt-8">
              Signalling the world<br /> I want to live in
            </DisplayHeading>
          </div>

          <div className="text-editorial-body text-editorial-muted/80 leading-relaxed space-y-6 max-w-prose">
            <p>
              Music is a commons to nurture, not a resource to mine.<br /><br />

              Yet capitalism is slowly draining what we share. Digital platforms accelerate this, harvesting us, selling extraction back as &quot;personalisation.&quot;<br /><br />

              But harvested data contains only implicit signals, echoes of what we care about. Like Plato&apos;s cave, algorithms feed us shadows rather than substance.<br /><br />

              We know this because we feel it. This disconnection is acute in art, but especially in music because music IS connection made physical, resonating in our ears.<br /><br />

              It&apos;s being commoditised and turned into content. Music&apos;s value reduced to profit while our commons collapses, artists fall silent and Spotify serves royalty-free filler over human expression. We are consumers, not listeners. Forced to resonate at frequencies that are not ours.<br /><br />

              What if tech resonated from us, rather than at us?<br /><br />

              What would need to be different?<br /><br />

              The answer lies in intention, made visible.<br /><br />

              Extraction can be one sided, but connection requires mutual intention.<br /><br />

              We must act with intention if we want vibrant culture. Art guides the way.<br /><br />

              When a beautiful song touches us, we feel the artist&apos;s intention, their deliberate care flowing into our ears. We resonate with their expression.<br /><br />

              When we intentionally signal care and attention for something, we attract others and calibrate together. The system begins to resonate from us, not at us.<br /><br />

              Our world is a collective endeavour requiring cooperation through clear intention, not capitalism&apos;s invisible hand.<br /><br />

              This is why I built Coral.<br /><br />

              I believe rebuilding our commons requires platforms that allow us to cooperate both <em>intentionally</em> and <em>transparently</em> at scale.<br /><br />

              Your coral, a collection of artists you back monthly, is a public signal declaring &quot;music matters, this should exist&quot;.<br /><br />

              The question is not &quot;What do I get?&quot; but &quot;What can we create together?&quot;<br /><br />

              Coral is my signal.<br /><br />

              Join me in tending music&apos;s commons.
            </p>
            <div className="flex justify-start">
              <img
                src={coralWordmarkBlack}
                alt="wearecoral"
                className="h-10 md:h-12"
              />
            </div>
          </div>
        </div>
      </EditorialSection>
    </div>
  );
};

export default Manifesto;
